const extensionPattern = /\s*(?:(?:доб(?:авочный)?|ext)\.?\s*|#)(\d+)\s*$/i;

const parenthesizedPhonePattern =
  /(?:(?:\+?7|8)\s*)?\(\d{3,5}\)\s*\d{1,3}(?:[\s-]\d{2}){2}(?:\s*(?:(?:доб(?:авочный)?|ext)\.?\s*|#)\d+)?/gi;

export type PhoneDetails = {
  display: string;
  href?: string;
};

const formatLocalNumber = (value: string) => {
  if (value.length <= 4) return value;

  return `${value.slice(0, -4)}-${value.slice(-4, -2)}-${value.slice(-2)}`;
};

export const splitPhoneNumbers = (phoneNumbers: string | null | undefined) => {
  const value = phoneNumbers?.trim();
  if (!value) return [];

  return value
    .replace(/,\s*(?=(?:\+?7|8)\s*(?:\(|\d))/g, ";")
    .split(/\s*(?:;|\||\r?\n)\s*/)
    .flatMap((part) => {
      const matches = part.match(parenthesizedPhonePattern) ?? [];
      return matches.length > 1 ? matches : [part];
    })
    .map((part) => part.trim())
    .filter(Boolean);
};

export const getPhoneDetails = (
  phoneNumber: string | null | undefined,
): PhoneDetails | undefined => {
  const value = phoneNumber?.trim();
  if (!value) return undefined;

  const extensionMatch = value.match(extensionPattern);
  const extension = extensionMatch?.[1];
  const baseNumber = extensionMatch
    ? value.slice(0, extensionMatch.index).trim()
    : value;
  const digits = baseNumber.replace(/\D/g, "");
  const hasExplicitForeignCountryCode =
    baseNumber.startsWith("+") && !/^\+7(?:\D|$)/.test(baseNumber);

  const nationalNumber = hasExplicitForeignCountryCode
    ? undefined
    : digits.length === 10
      ? digits
      : digits.length === 11 &&
          (digits.startsWith("7") || digits.startsWith("8"))
        ? digits.slice(1)
        : undefined;

  if (nationalNumber) {
    const parenthesizedAreaCode = baseNumber.match(/\((\d{3,5})\)/)?.[1];
    const areaCodeLength =
      parenthesizedAreaCode && nationalNumber.startsWith(parenthesizedAreaCode)
        ? parenthesizedAreaCode.length
        : 3;
    const areaCode = nationalNumber.slice(0, areaCodeLength);
    const localNumber = nationalNumber.slice(areaCodeLength);
    const extensionLabel = extension ? ` доб. ${extension}` : "";

    return {
      display: `+7 (${areaCode}) ${formatLocalNumber(localNumber)}${extensionLabel}`,
      href: `tel:+7${nationalNumber}${extension ? `;ext=${extension}` : ""}`,
    };
  }

  if (!digits) return { display: value };

  const prefix = baseNumber.startsWith("+") ? "+" : "";
  return {
    display: value,
    href: `tel:${prefix}${digits}${extension ? `;ext=${extension}` : ""}`,
  };
};

export const getPhoneHref = (phoneNumber: string | null | undefined) => {
  return getPhoneDetails(phoneNumber)?.href;
};
